import { h, Component, Fragment } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text, Localizer, withText } from 'preact-i18n';
import Markdown from 'preact-markdown';
import { QRCodeSVG } from 'qrcode.react';

import localUtil from '../../lib/util';
import fields from '../../lib/fields';
import { showReminderLabel } from '../../lib/invoiceReminderLabel';
import { VET_LABEL, isVetOnInvoiceEnabled, resolveInvoiceVets, getLineVetName } from '../../lib/invoiceVet';
import { lineDiscountForDisplay } from '../../lib/lineDiscount';

import FormFields from '../../components/form/fields';
import Input from '../../components/form/input';

@withText(props => ({
    orderNumber: <Text id='input.orderNumber-help'>orderNumber</Text>,
    name: <Text id='input.name-help'>name</Text>,
    description: <Text id='input.description-help'>description</Text>,
    installation: <Text id='input.installation-help'>installation</Text>,
    url: <Text id='input.url-help'>url</Text>,
}))
@observer
class SaleReceipt extends Component {
  	constructor(props) {
        super(props);
        this.state = {
            saleCustomer: null,
        };
    }

    loadAll = async () => {
        const { useCustomerFromSale } = this.props;
        const { saleStore, customerStore, userStore } = this.props.stores;
        const { newSale } = saleStore;

        // If useCustomerFromSale is true, load the customer from the sale
        let saleCustomer = null;
        if (useCustomerFromSale && newSale?.customer) {
            await customerStore.loadPublic(newSale.customer);
            saleCustomer = customerStore.customerPublic;
            this.setState({ saleCustomer });
        }

        // "Vis veterinær på faktura": the employee list is only loaded by the sale
        // route, so printing from Alle besøk needs it fetched here.
        const customer = saleCustomer || userStore.customer;
        if (isVetOnInvoiceEnabled(customer) && (!saleStore.employees || saleStore.employees.length === 0)) {
            await saleStore.loadEmployees();
        }
    }

    componentDidMount() {
        this.loadAll();
    }

    componentDidUpdate(prevProps) {
        const { saleStore } = this.props.stores;
        const { newSale } = saleStore;
        const { useCustomerFromSale } = this.props;

        // Reload if sale changes and we're using customer from sale
        if (useCustomerFromSale && newSale?.id && !this.state.saleCustomer) {
            this.loadAll();
        }
    }

    render() {
        const { week, isNew, drawerLevel, classnameContainer = 'receipt', useCustomerFromSale, suppressReminder = false } = this.props;
        const { sections, saleCustomer } = this.state;
        const { customerStore, userStore, saleStore } = this.props.stores;
        // Use sale's customer if prop is set and customer is loaded, otherwise use current user's customer
        const customer = (useCustomerFromSale && saleCustomer) ? saleCustomer : userStore.customer;
        const pricesExclMva = parseInt(customer?.featureFlags?.pricesExclMva, 10) === 1;
        const spacingMap = { loose: 'mt-5', normal: 'mt-3', compact: 'mt-2' };
        const sectionSpacing = spacingMap[customer?.featureFlags?.receiptSpacing] || 'mt-5';
        const { user, isAdmin } = userStore;

        const darkmode= util.getNestedValue(user, 'settings.darkmode');
        const {
            saleTotal = 0,
            saleTax = 0,
            newSale = {},
            currentPayments = [],
        } = saleStore;
        // const saleIsLocked = !!newSale.paymentMethod;
        // console.log({ newSale })

        const dueDate = new Date(newSale.dueDate || newSale.saleDate || new Date());
        if (customer.invoiceDueDays > 0 && !newSale.dueDate) {
            dueDate.setDate(dueDate.getDate() + customer.invoiceDueDays);
        }
        const { days: daysOverDue } = util.dateDiff(dueDate, new Date());
        const showReminder = showReminderLabel({ daysOverDue, reminders: newSale.reminders, suppressReminder });

        const restPayment = saleStore.sumRestPayments(currentPayments, saleStore.sumTotal(newSale.products, false), false, false);

        // Calculate totals directly for fallback when currentPayments is not loaded
        const calculateProductTotal = (products) => {
            if (!products || products.length === 0) return 0;
            return products.reduce((sum, p) => {
                const qty = p.qty || 1;
                let price = p.price || 0;
                if (p.medicine || p.fee || p.equipment || p.other) {
                    price = parseFloat(p.medicine || 0) + parseFloat(p.fee || 0) + parseFloat(p.equipment || 0) + parseFloat(p.other || 0);
                }
                let linePrice = qty * price;
                if (p.discount) {
                    linePrice -= linePrice * (p.discount / 100);
                }
                const taxAmount = linePrice * ((p.vat || 0) / 100);
                let totalPrice = linePrice + taxAmount;
                if (p.discountAmount) {
                    totalPrice -= (p.discountAmount * qty);
                }
                return sum + totalPrice;
            }, 0);
        };
        const rawProductTotal = calculateProductTotal(newSale.products);
        const saleProductTotal = saleStore.oreRoundingCashOnly
            ? Math.round((rawProductTotal + Number.EPSILON) * 100) / 100
            : Math.round(rawProductTotal);
        const paidAmount = newSale.paymentAmount || 0;
        const remainingAmount = saleProductTotal - paidAmount;

        // TICKET-01870: same truthy test as lineTotal(). Segment fields can be stored as "" and
        // must then count as "no segment", or the invoice prints four 0,00 cells for the line.
        const saleHasProductsWithGranularPrice = newSale.products && newSale.products.some(p => p.medicine || p.fee || p.equipment || p.other);

        // TICKET-01405: sum the price segments at the bottom of the invoice so
        // accountants don't have to add up the per-line columns by hand (those are
        // unit prices and cannot be summed anyway). Same order as the columns
        // above, and on the same VAT basis as the Total line below. Segments that
        // sum to zero are left out to keep the invoice clean.
        const segmentTotals = saleHasProductsWithGranularPrice
            ? saleStore.sumTotalParts(newSale.products, pricesExclMva)
            : { medicine: 0, fee: 0, equipment: 0, other: 0 };
        const showSegmentTotals = segmentTotals.medicine !== 0 || segmentTotals.fee !== 0
            || segmentTotals.equipment !== 0 || segmentTotals.other !== 0;
        const saleHasProductsWithSaleDate = newSale.products && newSale.products.some(p => p.saleDate);
        const saleHasProductsWithDiscount = newSale.products && newSale.products.some(p => p.discountAmount > 0);

        // "Vis veterinær på faktura": one vet on every line → header line; different
        // vets (samlefaktura) → under each product line. Same rule as the backend PDF.
        const getEmployee = id => saleStore.getEmployee(id);
        const vets = isVetOnInvoiceEnabled(customer)
            ? resolveInvoiceVets(newSale, getEmployee)
            : { header: '', perLine: false };

        // Sort products so shipping and invoiceFee products are always at the bottom
        const sortedProducts = newSale.products ? [...newSale.products].sort((a, b) => {
            const aIsBottom = a.type === 'shipping' || a.type === 'invoiceFee';
            const bIsBottom = b.type === 'shipping' || b.type === 'invoiceFee';
            if (aIsBottom && !bIsBottom) return 1;
            if (!aIsBottom && bIsBottom) return -1;
            return 0;
        }) : [];

        const saleIsPaidAndDone = newSale.paidDate || restPayment < 1;

        // console.log('newSale.dueDate', newSale.dueDate)
        // console.log('newSale.saleDate', newSale.saleDate)
        // console.log('dueDate', dueDate)
        if (!newSale.id) {
            return (<>
                <div class='d-flex flex-column justify-content-center align-items-center mt-5'>
                    <h2><Text id='dyrejournal.no-sale'>No sale</Text></h2>
                </div>
            </>);
        }

        const isCreditNote = newSale.paymentMethod === 'creditNote';
        const isSaleCredited = newSale.paymentMethod === 'saleCredited';
        const isWriteOff = newSale.paymentMethod === 'lost' || newSale.status === 1004;
        // Check both paymentMethod and currentPayments - paymentMethod might be set before payments are loaded
        const hasPartialPaymentMethod = newSale.paymentMethod === 'partial';
        const hasPartialPayments = currentPayments && currentPayments.length > 0 && restPayment >= 1;
        const isPartiallyPaid = hasPartialPaymentMethod || hasPartialPayments;
        // Receipt stamp for fully paid sales (not credit note, not credited, not partial, not write-off)
        const isReceipt = saleIsPaidAndDone && !isCreditNote && !isSaleCredited && !isPartiallyPaid && !isWriteOff;
        const showStamp = isCreditNote || isSaleCredited || isPartiallyPaid || isReceipt || isWriteOff;

        // Determine stamp color and text
        const getStampColor = () => {
            if (isWriteOff) return 'rgba(108, 117, 125, 0.45)'; // Gray
            if (isCreditNote) return 'rgba(220, 53, 69, 0.35)'; // Red
            if (isSaleCredited) return 'rgba(255, 193, 7, 0.45)'; // Yellow
            if (isPartiallyPaid) return 'rgba(0, 123, 255, 0.40)'; // Blue
            if (isReceipt) return 'rgba(40, 167, 69, 0.40)'; // Green
            return 'rgba(0, 123, 255, 0.40)';
        };
        const getStampText = () => {
            if (isWriteOff) return 'TAPSFØRT';
            if (isCreditNote) return 'KREDITTNOTA';
            if (isSaleCredited) return 'KREDITERT';
            if (isPartiallyPaid) return 'DELVIS BETALT';
            if (isReceipt) return 'KVITTERING';
            return '';
        };
        const stampColor = getStampColor();
        const stampText = getStampText();

        return (<>
            <div class={classnameContainer} style='position: relative;'>
                {showStamp && (
                    <div class='receipt-stamp' style={`
                        position: absolute;
                        top: 400px;
                        left: 50%;
                        transform: translate(-50%, -50%) rotate(-25deg);
                        z-index: 1000;
                        pointer-events: none;
                        font-size: ${isPartiallyPaid ? '3rem' : '4rem'};
                        font-weight: bold;
                        color: ${stampColor};
                        border: 8px solid ${stampColor};
                        border-radius: 12px;
                        padding: 15px 30px;
                        text-transform: uppercase;
                        white-space: nowrap;
                        text-align: center;
                        -webkit-print-color-adjust: exact;
                        print-color-adjust: exact;
                    `}>
                        {stampText}
                    </div>
                )}
                <div class={`d-flex flex-column`}>
                    <div class='receipt-header d-flex flex-row justify-content-between'>
                        <div class='d-flex flex-column'>
                            <div class='text-center d-flex align-items-center'>
                                {customer.images && customer.images.length > 0 && <img src={localUtil.displayImg(customer.images[0], this.props, '220x')} class='img-fluid' style='max-height: 100px;' />}
                            </div>
                            <h3 class='font-weight-lighter'>{customer.name}</h3>
                        </div>
                        <div class='d-flex flex-column align-items-end receipt-header-info' style='font-size: 0.85em;'>
                            {customer.url && <div><Text id='dyrejournal.website'>Website</Text>: <a href={customer.url}>{customer.url.replace(/^https?:\/\//, '')}</a></div>}
                            {customer.phone && <div><Text id='dyrejournal.phone'>Phone</Text>: {customer.phone}</div>}
                            {customer.invoiceHeaderText && <div class='mt-2' style='white-space: pre-line;'>{customer.invoiceHeaderText}</div>}
                        </div>
                    </div>
                    <div class={`receipt-customer d-flex flex-row justify-content-between ${sectionSpacing}`}>
                        <div class='d-flex flex-column'>
                            <div class='d-flex flex-row'>
                                <div class='font-weight-light'>
                                    <Text id='dyrejournal.customer-name'>Customer</Text>:
                                </div>
                                <div class='d-flex flex-column ms-2'>
                                    {newSale.visitors && newSale.visitors.length > 0 && newSale.visitors.map((visitor, i) => {
                                        return (<>
                                            <div>
                                                {localUtil.showName(visitor)}
                                            </div>
                                            <div>
                                                {visitor.address}
                                            </div>
                                            <div>
                                                {visitor.postalcode} {visitor.place}
                                            </div>
                                        </>);
                                    })}

                                    {newSale.animals && newSale.animals.length > 0 && newSale.animals.map((animal, i) => {
                                        return (<>
                                            <div>
                                                {animal.name} ({animal.species}/{animal.breed})
                                            </div>
                                        </>);
                                    })}
                                </div>
                            </div>
                            <div class='d-flex flex-row'>
                                <div class='font-weight-light'>
                                    <Text id='dyrejournal.customer-id'>Customer ID</Text>:
                                </div>
                                <div class='ms-2'>
                                    {newSale.visitor || newSale.visitors[0].id}
                                </div>
                            </div>
                        </div>
                        <div class='d-flex flex-column align-items-end'>
                            <div class='d-flex flex-row'><Text id='dyrejournal.date'>Date:</Text> {util.formatDate(newSale.saleDate || new Date(), { hour12: false, hour: '2-digit', minute: '2-digit', locale: 'nb-NO' })}</div>
                            {newSale.status === 1001 ? <>
                                <div class='d-flex flex-row'><Text id='dyrejournal.invoice-id'>Invoice ID:</Text> n/a</div>
                            </> : <>
                                {saleIsPaidAndDone ? <>
                                    <div class='d-flex flex-row'><Text id='dyrejournal.receipt-id'>Receipt ID:</Text> {newSale.saleNumber || 'n/a'}</div>
                                </> : <>
                                    <div class='d-flex flex-row'><Text id='dyrejournal.invoice-id'>Invoice ID:</Text> {newSale.saleNumber || 'n/a'}</div>
                                </>}
                            </>}
                            {vets.header && <div class='d-flex flex-row'>{VET_LABEL}: {vets.header}</div>}
                        </div>
                    </div>

                    <div class={`receipt-title d-flex flex-column align-items-center ${sectionSpacing}`}>
                        {newSale.status === 1001 ? <>
                            <h2><Text id='dyrejournal.priceoffer'>Price Offer</Text></h2>
                            <Text id='dyrejournal.priceoffer-help'>This is a price offer and should not be paid.</Text>
                        </> : <>
                            {newSale.paymentMethod === 'creditNote' ? <>
                                <h2>KREDITTNOTA</h2>
                            </> : newSale.paymentMethod === 'saleCredited' ? <>
                                <h2>SALG KREDITERT</h2>
                            </> : saleIsPaidAndDone ? <>
                                <h2><Text id='dyrejournal.receipt'>RECEIPT</Text></h2>
                            </> : <>
                                <h2>
                                    <Text id='dyrejournal.invoice'>INVOICE</Text>
                                    {showReminder ? <span class='ms-2'> - Påminnelse</span> : ''}
                                </h2>
                            </>}
                        </>}
                    </div>

                    <hr />

                    <div class={`receipt-products d-flex flex-column ${sectionSpacing}`}>
                        <div class=''>
                            <table class='table table-sm table-borderless'>
                                <thead>
                                    <tr>
                                        {saleHasProductsWithSaleDate && <>
                                            <th class='text-muted text-start'><nobr><Text id='dyrejournal.date'>Date</Text></nobr></th>
                                        </>}
                                        <th class='text-end'><Text id='dyrejournal.quantity'>Quantity</Text></th>
                                        <th><Text id='dyrejournal.item'>Item</Text></th>
                                        {saleHasProductsWithGranularPrice ? <>
                                            <th class='text-center'><nobr><Text id='dyrejournal.sale.medicine'>Medicine</Text></nobr></th>
                                            <th class='text-center'><nobr><Text id='dyrejournal.sale.fee'>Fee</Text></nobr></th>
                                            <th class='text-center'><nobr><Text id='dyrejournal.sale.equipment'>Equipment</Text></nobr></th>
                                            <th class='text-center'><nobr><Text id='dyrejournal.sale.other'>Other</Text></nobr></th>
                                        </> : <>
                                            <th class='text-end'><Text id='dyrejournal.price'>Price</Text></th>
                                        </>}
                                        {saleHasProductsWithDiscount && <th class='text-end'><Text id='dyrejournal.sale.discount-amount'>Discount</Text></th>}
                                        <th class='text-end'><Text id='dyrejournal.total'>Total</Text></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedProducts && sortedProducts.length > 0 && sortedProducts.map((product, i) => {
                                        return (<>
                                            <tr>
                                                {saleHasProductsWithSaleDate && <>
                                                    <td>
                                                        {product.saleDate && <>
                                                            {util.formatDate(product.saleDate, { locale: 'nb-NO' })}
                                                        </>}
                                                    </td>
                                                </>}
                                                <td class='text-end'>{product.qty}</td>
                                                <td>
                                                    {product.animal ? <>
                                                        <span class='font-weight-light text-muted me-2'>{product.animal.name}:</span>
                                                    </> : <></>}
                                                    {product.name}
                                                    {product.color ? <>
                                                        <span class='font-weight-light text-muted'> - <Text id='product.color'>Color</Text>: {product.color}</span>
                                                    </> : <></>}
                                                    {product.size ? <>
                                                        <span class='font-weight-light text-muted'> - <Text id='product.size'>Size</Text>: {product.size}</span>
                                                    </> : <></>}
                                                    {product.comment && <>
                                                        <br /><small>({product.comment})</small>
                                                    </>}
                                                    {vets.perLine && getLineVetName(product, newSale, getEmployee) && <>
                                                        <br /><small class='font-weight-light text-muted'>{VET_LABEL}: {getLineVetName(product, newSale, getEmployee)}</small>
                                                    </>}
                                                </td>
                                                {product.medicine || product.fee || product.equipment || product.other ? <>
                                                    <td class='text-end'><nobr>{saleStore.linePricePart(product, true, 'medicine', pricesExclMva)}</nobr></td>
                                                    <td class='text-end'><nobr>{saleStore.linePricePart(product, true, 'fee', pricesExclMva)}</nobr></td>
                                                    <td class='text-end'><nobr>{saleStore.linePricePart(product, true, 'equipment', pricesExclMva)}</nobr></td>
                                                    <td class='text-end'><nobr>{saleStore.linePricePart(product, true, 'other', pricesExclMva)}</nobr></td>
                                                </> : <>
                                                    <td class='text-end' colspan={saleHasProductsWithGranularPrice ? 4 : 1}><nobr>{util.format(product.price, 2)}</nobr></td>
                                                </>}
                                                {saleHasProductsWithDiscount && <>
                                                    <th class='text-end'>
                                                        <nobr>{lineDiscountForDisplay(product) ? `${util.format(lineDiscountForDisplay(product), 2, ',', ' ')}` : ''}</nobr>
                                                    </th>
                                                </>}
                                                <td class='text-end'><nobr>{saleStore.lineTotal(product, true, undefined, pricesExclMva)}</nobr></td>
                                            </tr>
                                        </>);
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div class='receipt-totals d-flex flex-row justify-content-end'>
                        <div class={`d-flex flex-column ${sectionSpacing} w-50 justify-content-end`}>
                            <table class='table table-sm table-borderless'>
                                <tbody>
                                    {showSegmentTotals && <>
                                        <tr>
                                            <td class='text-end text-muted' colspan='2'>
                                                <small><Text id='dyrejournal.invoice-segment-breakdown'>Breakdown</Text></small>
                                            </td>
                                        </tr>
                                        {segmentTotals.medicine !== 0 && <tr>
                                            <td class='text-end'><Text id='dyrejournal.sale.medicine'>Medicine</Text></td>
                                            <td class='text-end'><nobr>{util.format(segmentTotals.medicine, 2, ',', ' ')}</nobr></td>
                                        </tr>}
                                        {segmentTotals.fee !== 0 && <tr>
                                            <td class='text-end'><Text id='dyrejournal.sale.fee'>Fee</Text></td>
                                            <td class='text-end'><nobr>{util.format(segmentTotals.fee, 2, ',', ' ')}</nobr></td>
                                        </tr>}
                                        {segmentTotals.equipment !== 0 && <tr>
                                            <td class='text-end'><Text id='dyrejournal.sale.equipment'>Equipment</Text></td>
                                            <td class='text-end'><nobr>{util.format(segmentTotals.equipment, 2, ',', ' ')}</nobr></td>
                                        </tr>}
                                        {segmentTotals.other !== 0 && <tr>
                                            <td class='text-end'><Text id='dyrejournal.sale.other'>Other</Text></td>
                                            <td class='text-end'><nobr>{util.format(segmentTotals.other, 2, ',', ' ')}</nobr></td>
                                        </tr>}
                                        <tr>
                                            <td colspan='2'><hr class='my-1' /></td>
                                        </tr>
                                    </>}
                                    {pricesExclMva ? <>
                                        <tr>
                                            <td class='text-end'>Sum eks. MVA</td>
                                            <td class='text-end'>{saleStore.sumTotal(newSale.products, true, true)}</td>
                                        </tr>
                                        <tr>
                                            <td class='text-end'>MVA</td>
                                            <td class='text-end'>{saleStore.sumVat(newSale.products)}</td>
                                        </tr>
                                        <tr class='font-weight-bold'>
                                            <td class='text-end'>Totalt inkl. MVA</td>
                                            <td class='text-end'>{saleStore.sumTotal(newSale.products)}</td>
                                        </tr>
                                    </> : <>
                                        <tr>
                                            <td class='text-end'><Text id='dyrejournal.total'>Total</Text></td>
                                            <td class='text-end'>{saleStore.sumTotal(newSale.products)}</td>
                                        </tr>
                                        <tr>
                                            <td class='text-end'><Text id='dyrejournal.of-which-vat'>Of which VAT</Text></td>
                                            <td class='text-end'>{saleStore.sumVat(newSale.products)}</td>
                                        </tr>
                                    </>}
                                    {saleIsPaidAndDone && <>
                                        <tr>
                                            <td class='text-end'><Text id='dyrejournal.paid-with'>Paid with</Text></td>
                                            <td class='text-end'><i class={saleStore.getPaymentMethodIcon(newSale.paymentMethod)} /> {newSale.paymentMethod}</td>
                                        </tr>
                                        <tr>
                                            <td class='text-end'><Text id='dyrejournal.paid-date'>Paid date</Text></td>
                                            <td class='text-end'>{util.formatDate(newSale.paidDate, { hour12: false, hour: '2-digit', minute: '2-digit',  locale: 'nb-NO' })}</td>
                                        </tr>
                                    </>}

                                    {isPartiallyPaid && <>
                                        <tr>
                                            <td colspan='2'><hr class='my-1' /></td>
                                        </tr>
                                        <tr>
                                            <td class='text-end'><Text id='dyrejournal.partial-payment-total'>Paid so far</Text></td>
                                            <td class='text-end text-success font-weight-bold'>
                                                {currentPayments && currentPayments.length > 0
                                                    ? saleStore.sumTotalPayments(currentPayments)
                                                    : util.format(paidAmount, 2, ',', ' ')}
                                            </td>
                                        </tr>
                                        <tr style='background-color: rgba(0, 123, 255, 0.1); -webkit-print-color-adjust: exact; print-color-adjust: exact;'>
                                            <td class='text-end' style='font-size: 1.4em; font-weight: 700; padding: 8px 0; -webkit-print-color-adjust: exact; print-color-adjust: exact;'><Text id='dyrejournal.partial-payment-rest-payment'>Remaining</Text></td>
                                            <td class='text-end' style='font-size: 1.4em; font-weight: 700; padding: 8px 0; color: #007bff; -webkit-print-color-adjust: exact; print-color-adjust: exact;'>
                                                {currentPayments && currentPayments.length > 0
                                                    ? saleStore.sumRestPayments(currentPayments, saleStore.sumTotal(newSale.products, false))
                                                    : util.format(remainingAmount, 2, ',', ' ')}
                                            </td>
                                        </tr>
                                    </>}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {(newSale.paymentUuidv4 || newSale.isWeborder) ? <>
                        <div class='d-flex flex-column mt-5'>
                            <div class='d-flex flex-row justify-content-center'>

                                <QRCodeSVG value={`${newSale.uuidv4}`} size={256} />

                            </div>
                            <div class='d-flex flex-row justify-content-center'>
                                <small>Denne QR-koden brukes som billett. Ta vare på den dersom du har kjøpt en billett til ett arrangement.</small>
                            </div>

                        </div>
                    </> : <></>}

                    {currentPayments && currentPayments.length > 0 && <>
                        <div class={`d-flex flex-column ${sectionSpacing}`}>
                            <div class=''>
                                <span class='font-weight-bolder'><Text id='dyrejournal.payments'>Payments</Text></span>
                                <div class={`table-responsive`}>
                                    <table class='table table-sm table-borderless'>
                                        <thead>
                                            <tr>
                                                <th class='text-muted text-start'><nobr><Text id='dyrejournal.partial-payment-date'>Date</Text></nobr></th>
                                                <th class='text-muted text-center'><nobr><Text id='dyrejournal.partial-payment-method'>Method</Text></nobr></th>
                                                <th class='text-muted text-end'><nobr><Text id='dyrejournal.sale.amount'>Amount</Text></nobr></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {currentPayments.map((e, idx) => <>
                                                <tr>
                                                    <td>{util.formatDate(e.paidDate, { hour12: false, hour: '2-digit', minute: '2-digit', locale: 'nb-NO' })}</td>
                                                    <td class='text-center'><i class={saleStore.getPaymentMethodIcon(e.paymentMethod)} /> {e.paymentMethod}</td>
                                                    <td class='text-end'>{util.format(e.amount, 2, ',', ' ')}</td>
                                                </tr>
                                            </>)}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </>}

                    {!saleIsPaidAndDone && <div class='receipt-payment'>
                        <hr class='mt-2' />
                        {customer.invoiceInfo ? <>
                            <p style="margin: 0;"><b>{customer.invoiceInfo}</b></p>
                        </> : <></>}
                        <table width="100%" cellpadding="0" cellspacing="0">
                            <tbody>
                                {showReminder ? <>
                                    <tr>
                                        <td colspan='2'>
                                            <span class='font-weight-bold me-2'>Påminnelse.</span>
                                            <Text id='dyrejournal.invoice-overdue' fields={{ days: daysOverDue }}>Invoice is overdue by {daysOverDue} days.</Text>
                                        </td>
                                    </tr>
                                </> : <></>}
                                <tr>
                                    <td width="120" style="color: #808080; font-weight: 100;">
                                        <Text id='dyrejournal.due-date'>Due date</Text>:
                                    </td>
                                    <td>
                                        {util.formatDate(dueDate, { locale: 'nb-NO' })}
                                    </td>
                                </tr>
                                {isPartiallyPaid ? <>
                                    <tr>
                                        <td width="120" style="color: #007bff; font-weight: 700; font-size: 1.0em;" valign="top">
                                            <Text id='dyrejournal.remaining'>Remaining</Text>:
                                        </td>
                                        <td style='font-weight: 700; font-size: 1.2em; color: #007bff;'>
                                            {currentPayments && currentPayments.length > 0
                                                ? saleStore.sumRestPayments(currentPayments, saleStore.sumTotal(newSale.products, false))
                                                : util.format(remainingAmount, 2, ',', ' ')}
                                        </td>
                                    </tr>
                                </> : <>
                                    <tr>
                                        <td width="120" style="color: #808080; font-weight: 400; font-size: 1.5em;" valign="top">
                                            <Text id='dyrejournal.amount'>Amount</Text>:
                                        </td>
                                        <td style='font-weight: 700; font-size: 1.5em;'>
                                            {saleStore.sumTotal(newSale.products)}
                                        </td>
                                    </tr>
                                </>}
                                <tr>
                                    <td width="120" style="color: #808080; font-weight: 100;" valign="top">
                                        <Text id='dyrejournal.paid-to'>Paid to</Text>:
                                    </td>
                                    <td>
                                        {customer.name}
                                    </td>
                                </tr>
                                <tr>
                                    <td width="120" style="color: #808080; font-weight: 100;" valign="top">
                                        <Text id='dyrejournal.account-number'>Account number</Text>:
                                    </td>
                                    <td>
                                        {customer.invoiceAccount}
                                        {customer.invoiceIban && <>
                                            <br />
                                            <Text id='dyrejournal.iban'>IBAN:</Text> {customer.invoiceIban}
                                        </>}
                                        {customer.invoiceBic && <>
                                            <br />
                                            <Text id='dyrejournal.bic'>BIC:</Text> {customer.invoiceBic}
                                        </>}
                                    </td>
                                </tr>

                                {newSale.kid ? <>
                                    <tr>
                                        <td width="120" style="color: #808080; font-weight: 100;" valign="top">
                                            <Text id='dyrejournal.kid'>KID</Text>:
                                        </td>
                                        <td valign="top">
                                            {newSale.kid}
                                        </td>
                                    </tr>
                                </> : customer.invoiceHidePaymentReference ? <></> : customer.invoicePaymentReferenceText ? <>
                                    <tr>
                                        <td colspan='2' style="color: #808080;" valign="top">
                                            {customer.invoicePaymentReferenceText}
                                        </td>
                                    </tr>
                                </> : <>
                                    <tr>
                                        <td width="120" style="color: #808080; font-weight: 100;" valign="top">
                                            <Text id='dyrejournal.mark-payment-with'>Mark payment with</Text>:
                                        </td>
                                        <td valign="top">
                                            {newSale.status === 1001 ? <>
                                                <Text id='dyrejournal.invoice-id'>Invoice ID:</Text>: n/a
                                            </> : <>
                                                <Text id='dyrejournal.invoice-id'>Invoice ID:</Text>: {newSale.saleNumber || 'n/a'}
                                            </>}
                                        </td>
                                    </tr>
                                </>}
                            </tbody>
                        </table>
                    </div>}

                    {customer.invoiceFooterText && (
                        <div class='invoice-footer-text mt-3 mb-3 text-center' style='font-size: 10pt; white-space: pre-wrap; color: #666;'>
                            {customer.invoiceFooterText}
                        </div>
                    )}

                    <div class='receipt-footer d-flex flex-row justify-content-between mt-5'>
                        <div class='d-flex flex-column'>
                            <small>
                                <div class='mb-2 font-weight-bold'><Text id='dyrejournal.business-register'>The Business Register</Text></div>
                                <div>{customer.orgNumber}</div>
                                <div>{customer.name}</div>
                                {customer.orgNumber2 && <div>{customer.orgNumber}</div>}
                                {customer.name2 && <div>{customer.name}</div>}
                            </small>
                        </div>
                        <div class='d-flex flex-column'>
                            <small>
                                <div class='mb-2 font-weight-bold'><Text id='dyrejournal.address'>Address</Text></div>
                                <div>{customer.address}</div>
                                <div>{customer.postalcode} {customer.place}</div>
                            </small>
                        </div>
                        <div class='d-flex flex-column'>
                            <small>
                                <div class='mb-2 font-weight-bold'>{customer.fax ? <Text id='dyrejournal.phone-fax'>Phone/Fax</Text> : <Text id='dyrejournal.phone'>Phone</Text>}</div>
                                <div>{customer.phone}</div>
                                {customer.fax ? <div>{customer.fax}</div> : null}
                            </small>
                        </div>
                        <div class='d-flex flex-column'>
                            <small>
                                <div class='mb-2 font-weight-bold'><Text id='dyrejournal.internett'>Internet</Text></div>
                                <div>{customer.url}</div>
                                <div>{customer.email}</div>
                            </small>
                        </div>
                    </div>

                </div>

            </div>
        </>);
    }
}

export default SaleReceipt;
